/**
 * CompanyContext (Legacy Compatibility Layer)
 * 
 * This context is now a compatibility wrapper around CompanyMembershipContext
 * to maintain backward compatibility with existing code.
 * 
 * New code should use CompanyMembershipContext directly.
 */

import React, { createContext, useContext } from 'react';
import { useCompanyMembership } from './CompanyMembershipContext';
import { ensureHttps, logUrl, scanForHttpUrls } from '../utils/urlUtils';

const CompanyContext = createContext();

/**
 * useCompany hook - backward compatible wrapper
 * Returns the same API as before but uses CompanyMembershipContext internally
 */
export function useCompany() {
  const membership = useCompanyMembership();
  
  // Transform CompanyMembershipContext format to legacy CompanyContext format
  // This maintains backward compatibility with existing components
  return {
    // Legacy properties
    companyProfile: membership.currentCompany
      ? {
          ...membership.currentCompany,
          // Map name to companyName for backward compatibility
          companyName: membership.currentCompany.name || membership.currentCompany.companyName,
          // Map settings if they exist - flatten nested settings
          ...(membership.currentCompany.settings || {}),
        }
      : null,
    
    loading: membership.isLoading,
    error: membership.error,
    
    // Legacy updateCompanyProfile function
    updateCompanyProfile: membership.updateCompanyProfile,
    
    // New properties available (optional for backward compat)
    companyId: membership.companyId,
    currentUserRole: membership.currentUserRole,
    permissions: membership.permissions,
  };
}

/**
 * CompanyProvider (Legacy Compatibility Layer)
 * 
 * This provider is now just a pass-through to CompanyMembershipProvider.
 * All logic is handled by CompanyMembershipProvider.
 */
export function CompanyProvider({ children }) {
  // This is now handled by CompanyMembershipProvider in App.js
  // We just provide an empty provider for backward compatibility
  // Components using useCompany() will get data from CompanyMembershipContext
  return children;
} 